<script setup lang="ts">
import { Undo2 } from "@lucide/vue";
import { ModelType } from "@/features/BuildingProject";

const props = defineProps<{
  models: Array<ModelType>;
}>();

const emit = defineEmits<{
  keepModel: [model: ModelType];
}>();
</script>

<!--
  The models the page's Save will remove (RTLCA-5756). Its own group rather than a mark on the chip in
  place: the shared chip IS a toggle — the whole surface flips `isActive` and it carries a checkbox —
  and turning a model on while it is on its way out means nothing. The card already has the precedent
  next to this one, where "On the stream, not in this project" is hand-rolled chips for the same
  reason, and a group says once what a per-chip line would repeat.

  Amber, because that is this page's "changed, not saved" colour — the rail's dot, the Sync now note —
  and this is one of those changes, not an error. Red belongs to invalid input. Dashed for the reason
  the other two chip kinds are: it is not the state of things yet.
-->
<template>
  <div
    v-if="props.models.length > 0"
    class="flex flex-col gap-2">
    <span class="t-caption text-app-yellow-700 flex items-center gap-1.5">
      <span
        class="bg-app-yellow-500 h-1.5 w-1.5 shrink-0 rounded-full"
        aria-hidden="true" />
      To be removed when you save ({{ props.models.length }})
    </span>
    <ul class="flex flex-wrap gap-2">
      <li
        v-for="model in props.models"
        :key="model.name">
        <span
          class="rounded-8 border-app-yellow-300 bg-app-yellow-50 t-small flex items-center gap-1.5 border border-dashed py-2 pr-1 pl-3">
          <span class="text-app-yellow-800 line-through">{{ model.name }}</span>
          <!-- A strikethrough is not announced, and a colour is not a message. -->
          <span class="sr-only">will be removed when you save</span>
          <button
            type="button"
            class="focus-ring interactive-active rounded-4 t-caption-bold text-app-yellow-800 duration-fast ease-standard hover:bg-app-yellow-100 flex items-center gap-1 px-1.5 py-1 transition-colors"
            :aria-label="`Undo removing model ${model.name}`"
            @click="emit('keepModel', model)">
            <Undo2
              class="icon-xs shrink-0"
              :stroke-width="2"
              aria-hidden="true" />
            Undo
          </button>
        </span>
      </li>
    </ul>
    <p class="t-caption text-type-muted">Undo to keep one, or Discard to drop all these changes.</p>
  </div>
</template>
